import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Card,
  Table,
  Modal,
  Button,
  Combobox,
  DateInput,
  NumberInput,
  PhotoThumb,
} from '../../../components/common';
import { apiError } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { AUDIT_COLUMNS, sortByLabel } from '../../../config/columns';
import { expenseService, bankAccountService, expenseTypeService, getPhotoUrl } from '../../../services/resources';
import PhotoUpload from './PhotoUpload';

const fmt = (n) =>
  n === null || n === undefined || n === '' ? '' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 4 });

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => today().slice(0, 8) + '01';

const emptyForm = () => ({
  transaction_date: today(),
  bank_id: '',
  expense_id: '',
  amount: '',
  remark: '',
  idempotency_key: '',
});

export default function ExpensePage({ onToast }) {
  const { can } = useAuth();
  const canView = can('BIAYA', 'CanView');
  const canCreate = can('BIAYA', 'CanCreate');
  const canEdit = can('BIAYA', 'CanEdit');
  const canDelete = can('BIAYA', 'CanDelete');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState(firstOfMonth());
  const [dateTo, setDateTo] = useState(today());

  const [banks, setBanks] = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [preview, setPreview] = useState(null); // full-size photo modal

  const load = async (from = dateFrom, to = dateTo) => {
    setLoading(true);
    try {
      setRows(await expenseService.list(from, to));
    } catch (err) {
      onToast?.(apiError(err).message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const changeDateFrom = (v) => {
    setDateFrom(v);
    load(v, dateTo);
  };
  const changeDateTo = (v) => {
    setDateTo(v);
    load(dateFrom, v);
  };

  useEffect(() => {
    load();
    Promise.all([bankAccountService.list(), expenseTypeService.list()])
      .then(([b, e]) => {
        setBanks(b);
        setExpenseTypes(e);
      })
      .catch(() => {});
    // eslint-disable-next-line
  }, []);

  const bankOptions = sortByLabel(banks
    .filter((b) => !b.is_blocked)
    .map((b) => ({ value: b.id, label: b.bank_name })));
  const expenseOptions = sortByLabel(expenseTypes.map((e) => ({ value: e.id, label: e.name })));

  const FIELD_VALID = {
    transaction_date: (v) => !!v,
    bank_id: (v) => !!v,
    expense_id: (v) => !!v,
    amount: (v) => Number(v) > 0,
  };

  const set = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    setErrors((prev) => {
      if (!prev[k]) return prev;
      const check = FIELD_VALID[k];
      if (check && check(v)) {
        const next = { ...prev };
        delete next[k];
        return next;
      }
      return prev;
    });
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    const keys = ['doc_no', 'bank_name', 'expense_name', 'remark'];
    return rows.filter((r) => keys.some((k) => String(r[k] ?? '').toLowerCase().includes(term)));
  }, [rows, search]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm(), idempotency_key: crypto.randomUUID() });
    setErrors({});
    setSubmitError('');
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      transaction_date: String(row.transaction_date).slice(0, 10),
      bank_id: row.bank_id,
      expense_id: row.expense_id,
      amount: row.amount,
      remark: row.remark || '',
      idempotency_key: row.idempotency_key,
    });
    setErrors({});
    setSubmitError('');
    setOpen(true);
  };

  const validate = () => {
    const e = {};
    if (!form.transaction_date) e.transaction_date = 'Date is required';
    if (!form.bank_id) e.bank_id = 'Bank is required';
    if (!form.expense_id) e.expense_id = 'Expense type is required';
    if (!(Number(form.amount) > 0)) e.amount = 'Must be greater than 0';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (savingRef.current) return;
    if (!validate()) return;
    savingRef.current = true;
    setSaving(true);
    setSubmitError('');
    const payload = {
      transaction_date: form.transaction_date,
      bank_id: Number(form.bank_id),
      expense_id: Number(form.expense_id),
      amount: Number(form.amount),
      remark: form.remark || null,
      idempotency_key: form.idempotency_key,
    };
    try {
      if (editing) {
        await expenseService.update(editing.id, payload);
        onToast?.('Expense updated');
      } else {
        await expenseService.create(payload);
        onToast?.('Expense saved');
      }
      setOpen(false);
      load();
    } catch (err) {
      setSubmitError(apiError(err).message);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  // Fetch as a blob so the download works even though the image is served
  // cross-origin (localhost:5000 vs 3000), where the anchor download hint alone
  // would be ignored.
  const downloadPhoto = async () => {
    try {
      const res = await fetch(preview);
      const blob = await res.blob();
      const ext = blob.type === 'image/png' ? 'png' : 'jpg';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `expense-receipt.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      onToast?.('Could not download photo', 'error');
    }
  };

  const confirmDelete = async () => {
    try {
      await expenseService.remove(deleteTarget.id);
      onToast?.('Expense deleted');
      setDeleteTarget(null);
      load();
    } catch (err) {
      onToast?.(apiError(err).message, 'error');
    }
  };

  if (!canView) {
    return (
      <Card title="Expense">
        <p className="py-6 text-center text-sm text-gray-500">
          You don’t have permission to view this page.
        </p>
      </Card>
    );
  }

  const columns = [
    { key: 'doc_no', label: 'Doc No' },
    { key: 'transaction_date', label: 'Date', render: (r) => String(r.transaction_date).slice(0, 10) },
    { key: 'bank_name', label: 'Bank' },
    { key: 'expense_name', label: 'Expense Type' },
    { key: 'amount', label: 'Amount', render: (r) => fmt(r.amount), className: 'text-right' },
    { key: 'remark', label: 'Remark' },
    {
      key: 'photo',
      label: 'Photo',
      render: (r) => {
        const thumbUrl = `${getPhotoUrl('expense', r.id, 'thumb')}?t=${r.updated_on}`;
        return (
          <PhotoThumb
            key={thumbUrl}
            src={thumbUrl}
            onClick={() => setPreview(`${getPhotoUrl('expense', r.id)}?t=${r.updated_on}`)}
          />
        );
      },
    },
    ...AUDIT_COLUMNS,
    {
      key: '__a',
      label: 'Actions',
      className: 'text-right whitespace-nowrap',
      render: (r) => (
        <div className="flex justify-end gap-1.5">
          {canEdit && (
            <Button size="sm" variant="secondary" onClick={() => openEdit(r)}>
              Edit
            </Button>
          )}
          {canDelete && (
            <Button size="sm" variant="danger" onClick={() => setDeleteTarget(r)}>
              Delete
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <Card title="Expense" actions={canCreate ? <Button onClick={openCreate}>+ New</Button> : null}>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="w-40">
          <DateInput label="Date From" value={dateFrom} onChange={changeDateFrom} />
        </div>
        <div className="w-40">
          <DateInput label="Date To" value={dateTo} onChange={changeDateTo} />
        </div>
        <div className="flex flex-1 items-center gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            className="w-64 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
          />
          {search && (
            <Button type="button" variant="ghost" onClick={() => setSearch('')}>
              Clear
            </Button>
          )}
        </div>
      </div>

      <Table
        columns={columns}
        rows={filtered}
        loading={loading}
        maxHeight="60vh"
        className="rounded-md border border-gray-100"
      />
      <p className="mt-2 text-xs text-gray-400">{filtered.length} expense(s)</p>

      {/* Create / Edit modal */}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={`${editing ? 'Edit' : 'New'} Expense${editing ? ` — ${editing.doc_no}` : ''}`}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      >
        <form onSubmit={submit} className="space-y-3">
          {submitError && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{submitError}</div>
          )}
          <DateInput
            label="Date"
            required
            value={form.transaction_date}
            error={errors.transaction_date}
            onChange={(v) => set('transaction_date', v)}
          />
          <div className="grid grid-cols-2 gap-3">
            <Combobox
              label="Bank"
              required
              placeholder="-- select bank --"
              options={bankOptions}
              value={form.bank_id}
              error={errors.bank_id}
              onChange={(v) => set('bank_id', v)}
            />
            <Combobox
              label="Expense Type"
              required
              placeholder="-- select expense type --"
              options={expenseOptions}
              value={form.expense_id}
              error={errors.expense_id}
              onChange={(v) => set('expense_id', v)}
            />
          </div>
          <NumberInput
            label="Amount"
            required
            value={form.amount}
            error={errors.amount}
            onChange={(v) => set('amount', v)}
          />
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">Remark</span>
            <textarea
              value={form.remark}
              onChange={(e) => set('remark', e.target.value)}
              style={{ resize: 'none' }}
              className="h-16 w-full overflow-y-auto rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </label>
          {editing ? (
            <PhotoUpload expense={editing} onUploaded={load} onToast={onToast} />
          ) : (
            <p className="rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-500">
              Save the expense first, then re-open to upload a photo.
            </p>
          )}
        </form>
      </Modal>

      {/* Photo preview */}
      <Modal
        open={!!preview}
        onClose={() => setPreview(null)}
        title="Expense Receipt"
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setPreview(null)}>
              Close
            </Button>
            <Button onClick={downloadPhoto}>Download</Button>
          </>
        }
      >
        {preview && <img src={preview} alt="receipt" className="mx-auto max-h-[70vh] rounded" />}
      </Modal>

      {/* Delete confirmation */}
      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Expense"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDelete}>
              Delete
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-600">This will be permanently deleted. Continue?</p>
      </Modal>
    </Card>
  );
}
